import { fetchWithAdmin } from './fetchHelper';
import { firstTouchLabel, siteLabel } from '../pages/site-usage/shared/sourceLabels';

/** An AND of field/value pairs over the closed allowlist the API returns. */
export type AcquisitionCriteria = Record<string, string>;

/**
 * A criteria: acquisition fields, a behavioural clause, or BOTH.
 *
 * Both, because "came from this campaign and actually activated" is the
 * question the acquisition work exists to answer. The halves AND.
 */
export type SegmentCriteria = AcquisitionCriteria & {
  did?: string;
  withinDays?: number;
};

/** Does it name behaviour at all? True for a mixed criteria too. */
export function isBehavioural(c: SegmentCriteria): boolean {
  return typeof c.did === 'string' && c.did.length > 0;
}

/** The acquisition half alone. */
export function acquisitionPart(c: SegmentCriteria): AcquisitionCriteria {
  const { did: _d, withinDays: _w, ...rest } = c;
  return rest as AcquisitionCriteria;
}

/** A criteria as one line, the way it will be evaluated. Shown rather than
 *  summarised — a reader comparing two segments needs the exact conjunction. */
/** A criterion's value as a person reads it — "Instagram (ig)" — with the
 *  stored value kept, since that is what the segment matches on. */
function valueLabel(field: string, v: string): string {
  if (field === 'source') return firstTouchLabel(v);
  if (field === 'property') return siteLabel(v) === v ? v : `${siteLabel(v)} (${v})`;
  return v;
}

export function describeCriteria(c: SegmentCriteria): string {
  const parts = Object.entries(acquisitionPart(c)).map(([k, v]) => `${k} = ${valueLabel(k, String(v))}`);
  if (isBehavioural(c)) parts.push(`did ${c.did} within ${c.withinDays} days`);
  return parts.length ? parts.join('  AND  ') : '(empty)';
}

export interface Segment {
  id: string;
  name: string;
  description?: string;
  criteria: SegmentCriteria;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
}

export interface SegmentsResponse {
  /** What a criteria may name. Returned by the API so the client never
   *  hardcodes a list that can drift from what the server will accept. */
  fields: string[];
  segments: Segment[];
}

/** What each field was seen holding recently — suggestions, never a limit. */
export interface SegmentValues {
  since: string;
  days: number;
  /** Per field, most common first. Empty where nothing carried the field. */
  values: Record<string, string[]>;
  /** Act kinds anyone performed in the window — what `did` can name. */
  did: string[];
}

export const listSegmentValues = () =>
  fetchWithAdmin<SegmentValues>('/console/segments/values');

export const listSegments = () =>
  fetchWithAdmin<SegmentsResponse>('/console/segments');

export const createSegment = (body: { name: string; description?: string; criteria: SegmentCriteria }) =>
  fetchWithAdmin<Segment>('/console/segments', { method: 'POST', body: JSON.stringify(body) });

export const updateSegment = (id: string, body: { name?: string; description?: string; criteria?: SegmentCriteria }) =>
  fetchWithAdmin<Segment>(`/console/segments/${id}`, { method: 'PUT', body: JSON.stringify(body) });

export const deleteSegment = (id: string) =>
  fetchWithAdmin<{ deleted: boolean }>(`/console/segments/${id}`, { method: 'DELETE' });

/** What a segment's people did in a window — the tree for the "what they did"
 *  sunburst (backend `segmentActivity.ts`). Sized by times done; `visits` is
 *  distinct people. */
export interface SegmentActivity {
  from: string;
  to: string;
  segment: { id: string; name: string; criteria: Record<string, string | number> };
  people: number;
  active: number;
  tree: { name: string; events: number; visits: number; children: SegmentActivity['tree'][] };
  behaviourTruncated?: boolean;
}

export const getSegmentActivity = (segmentId: string, from: Date, to: Date) =>
  fetchWithAdmin<SegmentActivity>(
    `/console/segment-activity?segmentId=${encodeURIComponent(segmentId)}`
    + `&from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
  );

/** What a published link's people did — the same tree, for the people whose first
 *  touch was the link or whose session came through it (`?link=`). */
export const getLinkActivity = (slug: string, from: Date, to: Date) =>
  fetchWithAdmin<SegmentActivity>(
    `/console/segment-activity?link=${encodeURIComponent(slug)}`
    + `&from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
  );

/** How many people each segment holds now — `null` where it cannot be said per
 *  person (an `appSource` criterion). Backend `segmentPeople`. */
export const countSegments = () =>
  fetchWithAdmin<{ evaluatedAt: string; counts: Record<string, number | null> }>('/console/segments/counts');
